/**
 * TrackingResultPanel — renders the four UI states of `useTrackingQuery`:
 *   1. loading   → skeleton.
 *   2. not found → empty state ("No mail item found …").
 *   3. error     → error banner.
 *   4. ok        → status banner + chronological event timeline.
 *
 * The component itself is pure of fetch logic — it just renders. The hook
 * owns the cache key + queryFn.
 */
import type { JSX } from 'react';

import { formatEventTimeline } from './formatEventTimeline';
import { formatStatus } from './formatStatus';
import type { PublicMailItemView } from './PublicMailItemView';
import { useTrackingQuery } from './useTrackingQuery';

interface TrackingResultPanelProps {
  trackingNumber: string;
}

export function TrackingResultPanel({ trackingNumber }: TrackingResultPanelProps): JSX.Element {
  const query = useTrackingQuery(trackingNumber);

  if (query.isPending) {
    return <LoadingSkeleton trackingNumber={trackingNumber} />;
  }
  if (query.isError) {
    return <ErrorState message={query.error.message} />;
  }
  if (query.data === null) {
    return <NotFoundState trackingNumber={trackingNumber} />;
  }
  return <ResultBody item={query.data} />;
}

function LoadingSkeleton({ trackingNumber }: { trackingNumber: string }): JSX.Element {
  return (
    <section
      data-testid="tracking-loading"
      aria-busy="true"
      aria-live="polite"
      className="space-y-4"
    >
      <p className="text-sm text-slate-500">
        Looking up <span className="font-mono">{trackingNumber}</span>…
      </p>
      <div className="h-24 animate-pulse rounded-lg bg-slate-200" />
      <div className="h-48 animate-pulse rounded-lg bg-slate-100" />
    </section>
  );
}

function NotFoundState({ trackingNumber }: { trackingNumber: string }): JSX.Element {
  return (
    <section data-testid="tracking-not-found" className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">No mail item found with that tracking number</h2>
      <p className="mt-2 text-sm text-slate-600">
        We couldn&apos;t find anything matching{' '}
        <span className="font-mono">{trackingNumber}</span>. Double-check the
        number on your receipt — it should be 13 characters and end in NG.
      </p>
    </section>
  );
}

function ErrorState({ message }: { message: string }): JSX.Element {
  return (
    <section
      data-testid="tracking-error"
      role="alert"
      className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-900"
    >
      <h2 className="text-base font-semibold">Something went wrong</h2>
      <p className="mt-2 text-sm">
        We couldn&apos;t reach the tracking service right now. Please try again
        in a moment.
      </p>
      <p className="mt-2 text-xs text-rose-700/80">{message}</p>
    </section>
  );
}

function ResultBody({ item }: { item: PublicMailItemView }): JSX.Element {
  const status = formatStatus(item.status);
  const rows = formatEventTimeline(item.events);

  return (
    <section data-testid="tracking-result" className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Tracking number
            </p>
            <p className="font-mono text-lg font-semibold text-slate-900">
              {item.trackingNumber}
            </p>
            <p className="mt-1 text-sm text-slate-600">{item.serviceClassDisplay}</p>
          </div>
          <span
            data-testid="status-banner"
            data-status={item.status}
            data-tone={status.tone}
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${status.className}`}
          >
            {status.label}
          </span>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Registered</dt>
            <dd className="text-slate-900">{formatDateTime(item.registeredAt)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              Estimated delivery
            </dt>
            <dd className="text-slate-900">{formatDateTime(item.estimatedDelivery)}</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Tracking history</h3>
        {rows.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600">
            No tracking events recorded yet. Check back once the item enters the network.
          </p>
        ) : (
          <ol data-testid="event-timeline" className="mt-4 space-y-4">
            {rows.map((row, index) => (
              <li
                key={`${row.eventType}-${row.recordedAt}`}
                data-testid="event-row"
                className="flex gap-4"
              >
                <div className="flex flex-col items-center" aria-hidden="true">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-nipost-600" />
                  {index < rows.length - 1 ? (
                    <span className="mt-1 w-px flex-1 bg-slate-200" />
                  ) : null}
                </div>
                <div className="flex-1 pb-4">
                  <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                  <p className="text-xs text-slate-500">
                    {row.timeAgo}
                    {row.locationCode ? (
                      <>
                        {' · '}
                        <span data-testid="event-location" className="font-mono">
                          {row.locationCode}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-NG', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
